import React, { useState, useEffect, useRef } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { User } from "../types";
import { apiClient } from "../utils/apiClient";

interface HeaderProps {
  title?: string;
  children?: React.ReactNode;
}

const Header = ({ title, children }: HeaderProps) => {
  // App.tsxなどで提供されているContextからユーザー情報を取得
  const user = useOutletContext<User | null>();

  // ドロップダウンの開閉状態
  const [showDropdown, setShowDropdown] = useState(false);
  // ドロップダウンの要素を参照するためのRef
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 画面のどこかをクリックした時の処理（外側なら閉じる）
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // refが存在し、かつクリックされた要素がrefの内側でなければ閉じる
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    // イベントリスナーを追加
    document.addEventListener("mousedown", handleClickOutside);

    // クリーンアップ関数（コンポーネントが消える時にリスナーを削除）
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await apiClient.delete(`/session`);
    } catch (e) {
      console.error("Logout error:", e);
    }

    // ログアウト時はリロードして状態をクリア
    window.location.href = "/login";
  };

  const toggleDropdown = () => setShowDropdown(!showDropdown);

  return (
    <header
      className="bg-primary text-white mb-4 shadow-sm"
      style={{ minHeight: "64px", position: "sticky", top: 0, zIndex: 1000 }}
    >
      <div
        className="limit-width w-100 px-4 h-100 d-flex justify-content-between align-items-center"
        style={{ minHeight: "64px" }}
      >
        {/* --- 左側：ロゴ風デザイン --- */}
        <div className="d-flex align-items-center">
          <Link
            to="/"
            className="text-white text-decoration-none d-flex align-items-center hover-opacity"
          >
            <div
              className="bg-white text-primary rounded-circle d-flex align-items-center justify-content-center me-2"
              style={{ width: "32px", height: "32px" }}
            >
              <svg
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-calendar-check"
                viewBox="0 0 16 16"
              >
                <path d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
              </svg>
            </div>
            <div className="d-flex flex-column">
              <span className="fw-bold fs-5" style={{ lineHeight: "1.2" }}>
                業務報告システム
              </span>
              {title && (
                <span
                  className="small opacity-75"
                  style={{ fontSize: "0.75rem" }}
                >
                  {title}
                </span>
              )}
            </div>
          </Link>
        </div>

        {/* --- 右側：ユーザーメニュー（ドロップダウン） --- */}
        <div className="text-end text-white d-flex align-items-center gap-2">
          {children}

          {user && (
            // ここに ref={dropdownRef} を追加して、このdiv全体を監視対象にする
            <div className="position-relative" ref={dropdownRef}>
              {/* トリガーボタン */}
              <button
                onClick={toggleDropdown}
                className="btn btn-link text-white text-decoration-none p-0 d-flex align-items-center hover-opacity"
                style={{ border: "none" }}
              >
                <div className="text-end me-2 d-none d-sm-block">
                  <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                    {user.name}
                  </div>
                  {user.email && (
                    <div
                      className="small opacity-75"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {user.email}
                    </div>
                  )}
                </div>
                <div
                  className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center"
                  style={{ width: "40px", height: "40px" }}
                >
                  <svg
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                    <path
                      fillRule="evenodd"
                      d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"
                    />
                  </svg>
                </div>
              </button>

              {/* ドロップダウンメニュー本体 */}
              {showDropdown && (
                <div
                  className="card position-absolute end-0 mt-2 shadow-lg py-2"
                  style={{
                    width: "240px",
                    right: 0,
                    top: "100%",
                    zIndex: 1050,
                    border: "none",
                    textAlign: "left",
                  }}
                >
                  {/* --- セクション1: アプリ機能 --- */}
                  <div className="px-3 py-2 border-bottom mb-1 bg-light">
                    <small
                      className="text-muted fw-bold"
                      style={{ fontSize: "0.7rem" }}
                    >
                      メインメニュー
                    </small>
                  </div>

                  <Link
                    to="/"
                    className="dropdown-item px-3 py-2 d-flex align-items-center text-dark text-decoration-none"
                    onClick={() => setShowDropdown(false)}
                  >
                    <span className="me-2" style={{ width: "20px" }}>
                      🏠
                    </span>{" "}
                    TOP
                  </Link>

                  <Link
                    to="/entry"
                    className="dropdown-item px-3 py-2 d-flex align-items-center text-dark text-decoration-none"
                    onClick={() => setShowDropdown(false)}
                  >
                    <span className="me-2" style={{ width: "20px" }}>
                      ✏️
                    </span>{" "}
                    業務報告登録
                  </Link>

                  <Link
                    to="/list"
                    className="dropdown-item px-3 py-2 d-flex align-items-center text-dark text-decoration-none"
                    onClick={() => setShowDropdown(false)}
                  >
                    <span className="me-2" style={{ width: "20px" }}>
                      📋
                    </span>{" "}
                    業務報告一覧
                  </Link>

                  {/* --- セクション2: 管理者（権限がある場合のみ） --- */}
                  {user.is_admin && (
                    <>
                      <div className="dropdown-divider my-2 border-top"></div>
                      <div className="px-3 py-1">
                        <small
                          className="text-primary fw-bold"
                          style={{ fontSize: "0.7rem" }}
                        >
                          管理者用
                        </small>
                      </div>
                      <Link
                        to="/admin"
                        className="dropdown-item px-3 py-2 d-flex align-items-center text-primary fw-bold text-decoration-none"
                        onClick={() => setShowDropdown(false)}
                      >
                        <span className="me-2" style={{ width: "20px" }}>
                          ⚙️
                        </span>{" "}
                        管理者メニュー
                      </Link>
                    </>
                  )}

                  {/* --- セクション3: アカウント --- */}
                  <div className="dropdown-divider my-2 border-top"></div>

                  <button
                    onClick={handleLogout}
                    className="dropdown-item px-3 py-2 d-flex align-items-center text-danger w-100 text-start bg-transparent border-0"
                  >
                    <span className="me-2" style={{ width: "20px" }}>
                      🚪
                    </span>{" "}
                    ログアウト
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .hover-opacity:hover {
            opacity: 0.8;
            transition: opacity 0.2s;
        }
        .dropdown-item:hover {
            background-color: #f0f2f5;
        }
      `}</style>
    </header>
  );
};

export default Header;
